/**
 * Bottom nav for mobile and tablet. Hidden on desktop (≥ lg) where the
 * top Header carries all primary destinations.
 *
 * Layout: a 5-slot grid with the center theme toggle styled as the
 * visual anchor — slightly larger circular platform with a soft beam
 * tint and a faint glow, sitting above the row baseline. The four
 * outer slots are equal-width, icon + tiny label, with a pill-shaped
 * active state.
 *
 * Slots in order:
 *   1. Quran    → /quran
 *   2. Search   → /search
 *   3. Theme    (center, prominent — toggles light/dark)
 *   4. Kids     → /kids
 *   5. Portal   → /student-portal (RequireAuth bounces to /login)
 */

import { NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { useTheme } from '@/hooks/useTheme';
import {
  IconBookOpen,
  IconGraduationCap,
  IconMoon,
  IconSearch,
  IconSun,
  IconUser,
} from '@/components/home/icons';
import type { ComponentType, SVGProps } from 'react';

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

type SideItem = {
  key: string;
  label: string;
  Icon: IconCmp;
  to: string;
};

export function MobileNav() {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  const ThemeIcon = isDark ? IconSun : IconMoon;
  const themeLabel = isDark ? 'Light' : 'Dark';

  // Outer four items render via NavLink. The center theme slot is a
  // <button> so it can call the toggle handler.
  const left: SideItem[] = [
    { key: 'quran', label: 'Quran', Icon: IconBookOpen, to: '/quran' },
    { key: 'search', label: 'Search', Icon: IconSearch, to: '/search' },
  ];
  const right: SideItem[] = [
    { key: 'kids', label: 'Kids', Icon: IconGraduationCap, to: '/kids' },
    { key: 'portal', label: 'Portal', Icon: IconUser, to: '/student-portal' },
  ];

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'fixed inset-x-0 bottom-0 z-30 lg:hidden',
        // Top hairline + soft upward shadow so the bar reads as floating
        // above the page content.
        'border-t border-night-500/30 bg-night-900/95 backdrop-blur-xl',
        'shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.5)]',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      {/* Five-column grid. Outer columns are equal width; the center
          column is `auto` so its larger button can size to its content
          without disrupting the spacing of the four side items. */}
      <ul className="mx-auto grid h-[68px] max-w-md grid-cols-[1fr_1fr_auto_1fr_1fr] items-center px-2">
        {left.map((item) => (
          <SideSlot key={item.key} item={item} />
        ))}

        {/* Center — theme toggle. Sits inside its own platform: a 56×56
            circle with a beam-tinted border + soft inner glow. Anchors
            the bar visually and gives the user a one-tap path to switch
            themes from anywhere. */}
        <li className="flex justify-center">
          <button
            type="button"
            onClick={toggle}
            aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
            title={isDark ? 'Light theme' : 'Dark theme'}
            className={cn(
              'group relative flex flex-col items-center justify-center gap-1',
              // The "platform" — a 56px circle slightly raised above the
              // outer row baseline by a tiny -mt to suggest elevation.
              '-mt-1 h-14 w-14 rounded-full',
              'border border-beam/40 bg-beam/10',
              'shadow-[0_0_16px_-2px_rgba(0,229,255,0.35),inset_0_0_12px_rgba(0,229,255,0.15)]',
              'transition-all duration-200',
              'hover:border-beam/60 hover:bg-beam/15',
              'active:scale-95',
            )}
          >
            <ThemeIcon className="h-6 w-6 text-beam" aria-hidden="true" />
            <span className="sr-only">{themeLabel} mode</span>
          </button>
        </li>

        {right.map((item) => (
          <SideSlot key={item.key} item={item} />
        ))}
      </ul>
    </nav>
  );
}

/**
 * One of the four outer slots. Renders icon + small label inside a
 * 48px-tall vertical stack. Active route gets a soft beam-tinted pill
 * background behind the icon, plus beam-colored icon + label.
 */
function SideSlot({ item }: { item: SideItem }) {
  return (
    <li className="flex justify-center">
      <NavLink
        end={item.to === '/'}
        to={item.to}
        className={({ isActive }) =>
          cn(
            'group flex h-14 w-14 flex-col items-center justify-center gap-1 rounded-2xl transition-colors',
            isActive
              ? 'text-beam'
              : 'text-night-300 hover:text-night-50',
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* Icon container — gets a beam-tinted pill in active state. */}
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-xl transition-colors',
                isActive
                  ? 'bg-beam/15 text-beam shadow-[0_0_10px_-2px_rgba(0,229,255,0.4)]'
                  : 'text-night-300 group-hover:text-night-50',
              )}
            >
              <item.Icon className="h-[20px] w-[20px]" aria-hidden="true" />
            </span>
            <span
              className={cn(
                'text-[10px] font-medium tracking-wide leading-none',
                isActive ? 'text-beam' : 'text-night-300 group-hover:text-night-50',
              )}
            >
              {item.label}
            </span>
          </>
        )}
      </NavLink>
    </li>
  );
}
